import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Divider } from '@/components/ui/daisy';
import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { useFormRegistration } from '@/data/hook/question/useFormRegistration';
import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import { PointsSection } from '../fields/PointsSection';

import { QuestionCreateDTO, PositionalAnswerCreateDTO } from '@/models/Answer';
import { FeedbackCreateDTO } from '@/models/Feedback';
import { DNDPositionalQuestion } from '@/models/Question';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
  TranslatableDict,
} from '@/models/TranslatableField';
import {
  adapterPositionalAnswers,
  adapterPositionalQuestions,
} from '@/utils/questions/adapterAnswersDTO';
import { adapterFeedbacks } from '@/utils/questions/adapterFeedbacks';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import { QuestionContextField } from '../fields/QuestionContextField';
import { QuestionTitleField } from '../fields/QuestionTitleField';
import { QuestionFormContainer } from '../QuestionFormContainer';
import QuestionPreview from '@/components/question/forms/QuestionPreview';
import { useCombinationPreviewQuestion } from '@/data/hook/question/useCombinationPreviewQuestion';
import AnswerColumnForm from './AnswerColumnForm';
import QuestionColumnForm from './QuestionColumnForm';

export type CombinationFormFields = {
  title: TranslatableDict;
  context: TranslatableDict;
  questions: QuestionCreateDTO[];
  answers: PositionalAnswerCreateDTO[];
  feedbacks: FeedbackCreateDTO[];
  maxGrade?: number;
};

type CombinationFormProps = {
  isActive?: boolean;
  question: DNDPositionalQuestion;
  isLoading?: boolean;
  onSubmit: (data: CombinationFormFields) => Promise<DNDPositionalQuestion>;
};

export default function CombinationForm({
  isActive = true,
  question,
  isLoading,
  onSubmit,
}: CombinationFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.combinationForm',
  });

  const { identifier } = question;
  const questionColumn = question.questionColumn;
  const answerColumn = question.answerColumn;

  const [activeLanguage, setActiveLanguage] =
    useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const gradeCtx = useQuestionExamGradeCtx();
  const maxGradeDefault = gradeCtx?.getGradeForQuestion(question)?.maxGrade;

  const defaultValues = useMemo<CombinationFormFields>(
    () => ({
      title: normalizeTranslatableField(question?.title),
      context: normalizeTranslatableField(question?.context),
      questions: adapterPositionalQuestions(questionColumn?.items ?? []),
      answers: adapterPositionalAnswers(answerColumn?.items ?? []),
      feedbacks: adapterFeedbacks(question?.feedbacks ?? []),
      maxGrade: maxGradeDefault,
    }),
    [question, questionColumn?.items, answerColumn?.items, maxGradeDefault],
  );

  const { submitForm } = useQuestionFormsOrchestrator();

  const methods = useForm<CombinationFormFields>({ defaultValues });
  const { watch, trigger, reset, formState } = methods;

  const { hasChanges, handleReset } = useFormRegistration({
    identifier,
    submit: onSubmit,
    validate: trigger,
    watch,
    reset,
    formState,
    defaultValues,
  });

  const realTimeValues = watch();
  const previewQuestion = useCombinationPreviewQuestion(realTimeValues);

  return (
    <div className="flex gap-6">
      <QuestionFormContainer
        methods={methods}
        activeLanguage={activeLanguage}
        onLanguageChange={setActiveLanguage}
        onSubmit={e => {
          e.preventDefault();
          submitForm(identifier);
        }}
        isLoading={isLoading}
        hasChanges={hasChanges}
        onCancel={handleReset}
      >
        <div className="flex flex-col gap-4">
          <QuestionTitleField
            activeLanguage={activeLanguage}
            placeholder={t('titlePlaceholder')}
          />
          <QuestionContextField
            activeLanguage={activeLanguage}
            isActive={isActive}
          />
        </div>

        <Divider />

        <QuestionColumnForm identifier={identifier} />

        <Divider />

        <AnswerColumnForm />

        <PointsSection />
      </QuestionFormContainer>

      <div className="flex-1">
        <QuestionPreview question={previewQuestion} />
      </div>
    </div>
  );
}
